import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import * as api from "../api";
import { useTx } from "../i18n";
import FaraAcces from "./FaraAcces";
import ProiectInexistent from "./ProiectInexistent";

export type Administrator = { name: string | null; email: string };

/** Ce ai voie să faci pe proiectul deschis — pentru ECRANE, nu pentru server.
 *
 *  Serverul refuză oricum ce nu ți-e permis (`projects.cere_rol`), iar refuzul
 *  lui e cel care contează: nimic din ce e aici nu apără datele. Ce apără e
 *  timpul omului. Fără asta, cine are drept de citire poate trage de colțurile
 *  parcelei, poate umple un formular, poate apăsa «promovează» — și află abia
 *  la salvare că nimic nu s-a scris. Gestul e permis până la capăt, apoi
 *  anulat: exact felul de interfață care pare stricată.
 *
 *  Rolul se cere O SINGURĂ DATĂ, aici, și îl citesc toate ecranele din
 *  proiect. Cu un hook care cere singur pe fiecare pagină, un drum ca
 *  Dosar → Soluții → Costuri ar face trei cereri identice, iar ecranele
 *  compuse (Soluții ține Board-ul, care ține modalul) încă pe atâtea.
 */

type Valoare = {
  rol: api.ProjectRole | null;
  potScrie: boolean;
  potAdministra: boolean;
  gata: boolean;
  /** Tipul proiectului vine din ACEEAȘI citire a proiectului: de el își aleg
   *  pașii dosarului, lanțul din antet și dosarul ce arată. `null` cât timp
   *  proiectul nu s-a citit încă. */
  tip: api.TipProiect | null;
  /** Cum pornește structura pe proiectul ăsta (v. `api.GeometrieMacro`). */
  geometrieMacro: api.GeometrieMacro | null;
};

/** Ce trimite serverul la 403 „no_access" — v. `projects._fara_acces`. */
type Refuz = {
  project?: { name?: string };
  org?: { name?: string };
  administratori?: Administrator[];
};

// Implicit: NU poți scrie. În sens invers, ecranul ar clipi cu butoanele
// active și le-ar stinge sub mână.
const IMPLICIT: Valoare = { rol: null, potScrie: false, potAdministra: false,
                            gata: false, tip: null, geometrieMacro: null };

const Context = createContext<Valoare>(IMPLICIT);

export function RolProiectProvider({ projectId, children }:
                                   { projectId: string; children: ReactNode }) {
  const [stare, setStare] = useState<Valoare>(IMPLICIT);
  const [refuz, setRefuz] = useState<Refuz | null>(null);
  const [inexistent, setInexistent] = useState(false);

  useEffect(() => {
    let viu = true;
    setStare(IMPLICIT);
    setRefuz(null);
    setInexistent(false);
    api.getProject(projectId)
      .then((p) => viu && setStare({
        rol: p.role,
        potScrie: p.role === "editor" || p.role === "admin",
        potAdministra: p.role === "admin",
        gata: true,
        tip: p.tip,
        geometrieMacro: p.geometrie_macro ?? "generata",
      }))
      .catch((e) => {
        if (!viu) return;
        // Două refuzuri, două ecrane — și amândouă țin locul ÎNTREGII pagini.
        //
        // 403 „no_access": proiectul există și ești în organizația lui, doar nu
        // ești pe el. Are ce spune, și o spune (`FaraAcces`).
        //
        // 404: pentru contul ăsta proiectul nu există. Aici carcasa NU are voie
        // să se deseneze: din bara de discipline, din pașii dosarului și din
        // șina din stânga se citea că proiectul există și cum arată — adică
        // exact ce ascunde 404-ul de la server. Un id ghicit primea o hartă.
        //
        // Restul (rețea căzută, 500) rămân ca înainte: `gata` pus, ca banda de
        // rol să nu rămână agățată, și fiecare filă își arată eroarea ei — acolo
        // chiar e o defecțiune, nu un refuz.
        if (e instanceof api.ApiError && e.status === 403 && e.code === "no_access") {
          setRefuz((e.date ?? {}) as Refuz);
        } else if (e instanceof api.ApiError && e.status === 404) {
          setInexistent(true);
        }
        setStare({ ...IMPLICIT, gata: true });
      });
    return () => { viu = false; };
  }, [projectId]);

  // Ecranele țin locul COPIILOR, nu al unei file: refuzul e al proiectului, deci
  // n-are sens să poți umbla prin taburile lui.
  if (inexistent) return <ProiectInexistent />;
  if (refuz) {
    return (
      <FaraAcces proiect={refuz.project?.name} organizatie={refuz.org?.name}
                 administratori={refuz.administratori ?? []} />
    );
  }

  return <Context.Provider value={stare}>{children}</Context.Provider>;
}

/** Fără argument: rolul vine din provider-ul montat de Shell pe rutele de
 *  proiect. În afara lui întoarce «nu poți scrie», ceea ce e răspunsul corect
 *  pentru un ecran care nu e într-un proiect. */
export function useRolProiect(): Valoare {
  return useContext(Context);
}

/** Tipul proiectului deschis; `null` cât timp nu s-a citit, și în afara unui
 *  proiect. */
export function useGeometrieMacro(): api.GeometrieMacro | null {
  return useContext(Context).geometrieMacro;
}

export function useTipProiect(): api.TipProiect | null {
  return useContext(Context).tip;
}

/** Banda care spune de ce lipsesc butoanele. Fără ea, un ecran fără unelte
 *  arată ca unul stricat, nu ca unul pe care n-ai drepturi. */
export function BandaDoarCitire() {
  const { rol, gata } = useRolProiect();
  const tx = useTx();
  if (!gata || rol !== "viewer") return null;
  return <div className="banda-citire">{tx("rol.banda_citire")}</div>;
}
